/**
 * AI Chat API Client
 * Story: E007-S028
 */

import { apiClient } from './apiClient';
import type {
  Conversation,
  ConversationsResponse,
  CreateConversationResponse,
  SendMessageResponse,
  SelectableAssetsResponse,
  QuickQuestionsResponse,
  ConversationContext,
} from '../types/ai-chat';

const BASE_URL = '/api/v1/ai-chat';

/**
 * Create a new conversation
 */
export async function createConversation(
  initialContext?: ConversationContext
): Promise<CreateConversationResponse> {
  const response = await apiClient.post(`${BASE_URL}/conversations`, {
    initial_context: initialContext,
  });
  return response.data;
}

/**
 * Get list of conversations
 */
export async function getConversations(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  favorites_only?: boolean;
}): Promise<ConversationsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.offset) searchParams.set('offset', String(params.offset));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.favorites_only) searchParams.set('favorites_only', 'true');

  const response = await apiClient.get(
    `${BASE_URL}/conversations?${searchParams.toString()}`
  );
  return response.data;
}

/**
 * Get a single conversation with messages
 */
export async function getConversation(
  conversationId: string
): Promise<Conversation> {
  const response = await apiClient.get(
    `${BASE_URL}/conversations/${conversationId}`
  );
  return response.data;
}

/**
 * Send a message and get AI response
 */
export async function sendMessage(
  conversationId: string,
  message: string,
  contextUpdate?: ConversationContext
): Promise<SendMessageResponse> {
  const response = await apiClient.post(
    `${BASE_URL}/conversations/${conversationId}/messages`,
    {
      message,
      context_update: contextUpdate,
    }
  );
  return response.data;
}

/**
 * Update conversation (title, favorite status)
 */
export async function updateConversation(
  conversationId: string,
  updates: {
    title?: string;
    is_favorite?: boolean;
    context?: ConversationContext;
  }
): Promise<{ success: boolean }> {
  const response = await apiClient.patch(
    `${BASE_URL}/conversations/${conversationId}`,
    updates
  );
  return response.data;
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  conversationId: string
): Promise<{ success: boolean }> {
  const response = await apiClient.delete(
    `${BASE_URL}/conversations/${conversationId}`
  );
  return response.data;
}

/**
 * Get selectable assets for context
 */
export async function getSelectableAssets(params?: {
  search?: string;
  platform?: string;
  date_start?: string;
  date_end?: string;
}): Promise<SelectableAssetsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.platform) searchParams.set('platform', params.platform);
  if (params?.date_start) searchParams.set('date_start', params.date_start);
  if (params?.date_end) searchParams.set('date_end', params.date_end);

  const response = await apiClient.get(
    `${BASE_URL}/selectable-assets?${searchParams.toString()}`
  );
  return response.data;
}

/**
 * Get quick questions
 */
export async function getQuickQuestions(): Promise<QuickQuestionsResponse> {
  const response = await apiClient.get(`${BASE_URL}/quick-questions`);
  return response.data;
}
